import { gql } from "graphql-request";

/**
 * A function to make a GraphQL request.
 * 
 * @param query - The GraphQL query string.
 * @param variables - An optional object of variables to pass to the query.
 * @returns The result of the GraphQL request.
 */
const gqlRequest = async (query: string, variables = {}) => {
  const { $gqlRequest } = useNuxtApp();
  return await ($gqlRequest as any)(query, variables);
};

/**
 * Searches for number inventories based on country prefix, number-like pattern, and area prefix.
 * 
 * @param countryPrefix - The country prefix to filter the search.
 * @param numberLike - An optional pattern to search for numbers.
 * @param areaPrefix - The area prefix to filter the search.
 * @param isConferenceNumber - Whether to search conference-call capable numbers.
 * @returns A list of number inventories.
 */
export const numberInventoriesSearch = async (
  countryPrefix: string,
  numberLike: string | null,
  areaPrefix: string,
  isConferenceNumber = false,
) => {
  const query = gql`
    query queryInventory ($countryPrefix: String!, $numberLike: String, $areaPrefix: String!, $isConferenceNumber: Boolean) {
      bpnumbersInventoriesSearch (input: {countryPrefix: $countryPrefix, numberLike: $numberLike, areaPrefix: $areaPrefix, isConferenceNumber: $isConferenceNumber}) {
        numbers {
          id
          did
        }
        meta {
          externalGroupId
          groupId
          needsRegistration
          providerCode
          reservationTimeoutSec
        }
      }
    }
  `;

  const variables = {
    countryPrefix,
    numberLike: numberLike || '',
    areaPrefix,
    isConferenceNumber,
  };

  const data = await gqlRequest(query, variables);
  return data;
};

/**
 * Retrieves a list of BP numbers.
 * 
 * @returns A list of BP numbers with their details.
 */
export const getBpNumbersList = async () => {
  const query = gql`
    query {
      bpnumbers {
        _id
        callerTune {
          bucket
          fileName
        }
        callerTuneIsActive
        did
        didTitle
        ownerId
        ownerName
        invitedTo
        joinedAt
        subscriptionEndAt
        subscriptionStartAt
        destinations {
          number
          title
          createdAt
          type
          status
          isEnabled
          agentId
          u {
            email
          }
        }
      }
    }`;

  try {
    const data = await gqlRequest(query);
    return (data as any).bpnumbers ?? [];
  } catch (error: any) {
    // The API returns a GraphQL error (not an HTTP error) when the user has no
    // numbers yet. Treat that as an empty list rather than a hard failure.
    const isNotFound = error?.response?.errors?.some((e: any) =>
      e.message?.toLowerCase().includes("no bpnumbers found")
    );
    if (isNotFound) return [];
    throw error;
  }
};

/**
 * Leaves an invited number based on DID.
 *
 * @param did - The DID of the invited number to leave.
 */
export const unlinkInvitedNumber = async (did: string) => {
  const query = gql`
    mutation ($did: String!) {
      unlinkInvitedNumber(input: { did: $did }) {
        did
      }
    }
  `;

  const variables = {
    did,
  };

  await gqlRequest(query, variables);
};

/**
 * Enables or disables a single destination on a BP number.
 * Exactly one of destinationNumber, email, or agentId must be provided to identify the target.
 *
 * @param did - The DID of the BP number.
 * @param isEnabled - The new enabled state.
 * @param identifiers - One of destinationNumber, email, or agentId that identifies the destination.
 */
export const toggleDestinationEnabled = async (
  did: string,
  isEnabled: boolean,
  identifiers: { destinationNumber?: string; email?: string; agentId?: string },
) => {
  const query = gql`
    mutation ToggleDestinationEnabled(
      $did: String!
      $destinationNumber: String
      $email: String
      $agentId: String
      $isEnabled: Boolean!
    ) {
      updateBpNumberDestination(input: {
        did: $did
        destinationNumber: $destinationNumber
        email: $email
        agentId: $agentId
        isEnabled: $isEnabled
      }) {
        _id
      }
    }
  `;

  await gqlRequest(query, { did, isEnabled, ...identifiers });
};

/**
 * Retrieves a list of reserved BP numbers.
 * 
 * @returns A list of reserved BP numbers and their reservation details.
 */
export const getReservedBpNumbers = async () => {
  const query = gql`
    query {
      bpnumbersInventories (query: {
        reservation_exists: true
      })  {
        _id
        isExternal
        did
        reservation {
          startAt
          endAt
        }
        groupId
      }
    }`;

  const data = await gqlRequest(query);
  return data.bpnumbersInventories;
};

/**
 * Unreserves a BP number based on its DID.
 * 
 * @param did - The DID of the BP number to unreserve.
 * @returns The unreserved BP number details.
 */
export const unReserveBpNumber = async (did: string) => {
  const query = gql`
    mutation ($did: String!) {
      unreserveBpNumber (input: { did: $did }) {
        did,
        reservation {
          startAt
          endAt
          reserver
        }
      }
    }`;

  const variables = {
    did
  };

  const data = await gqlRequest(query, variables);
  return data.unreserveBpNumber as UnreserveBpNumber;
};

/**
 * Reserve a BP number with retry mechanism
 *
 * @param {string} did - The DID to reserve.
 * @param {string} numberGroupId - The number group ID.
 * @param {string} providerCode - The provider code.
 * @param {string} [didId] - The DID ID.
 * @param {number} [retries=3] - The number of retry attempts.
 * @returns {Promise<any>} - A promise that resolves to the response of the reservation request.
 */
export const reserveBpNumber = async (
  did: string,
  numberGroupId: string,
  providerCode: string,
  didId?: string,
  retries = 3
): Promise<unknown> => {
  const query = `
    mutation makeNumberReservation($did: String!, $numberGroupId: String!, $providerCode: String!, $didId: String) {
      reserveBpNumber(input: {
        did: $did,
        numberGroupId: $numberGroupId,
        providerCode: $providerCode,
        didId: $didId
      }) {
        did
      }
    }
  `;

  const variables = { did, numberGroupId, providerCode, didId };

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await gqlRequest(query, variables);
      return response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (attempt === retries || !error.message.includes('WriteConflict')) {
        throw error;
      }
      console.warn(`Attempt ${attempt} failed. Retrying...`);
    }
  }
};

/**
 * Interface representing metadata associated with a number group.
 */
export interface NumberGroupMeta {
  needsRegistration: boolean;
}

/**
 * Retrieves details of a specific number group based on its ID.
 * 
 * @param id - The ID of the number group to retrieve.
 * @returns The details of the number group.
 */
export const getNumberGroup = async (id: string) => {
  const query = gql`
    query ($id: ObjectId) { 
      numberGroup (query: { 
      _id: $id, 
      meta: { 
        isAvailable : true
      }
      }) {
        _id
        prefix
        areaName
        city
        country {
          iso
          name
          prefix
        }
        meta {
          needsRegistration
        }
      }
    }`;

  const variables = {
    id
  };

  const data = await gqlRequest(query, variables);
  return data.numberGroup as NumberGroup;
};

/**
 * Retrieves a list of all available number groups.
 * 
 * @returns A list of number groups with their details.
 */
export const getNumberGroups = async () => {
  const query = gql`
    query {
      numberGroups (query: {} ) {
        _id
        prefix
        areaName
        city
        country {
          iso
          name
          prefix
        }
        meta {
          needsRegistration
        }
      }
    }`;

  const data = await gqlRequest(query);
  return data.numberGroups as NumberGroup[];
};
